import { BrowseFilmTileFragment, FilmSessionFragment, UseProgramFilmsQuery } from "@generated-types"
import { useHydrating, useRestorableState, useRouter, useSearchParams } from "eddev/routing"
import { hash } from "object-code"
import { startTransition, useEffect, useMemo, useState } from "react"
import { useCallbackRef } from "../../hooks/useCallbackRef"
import {
  DEFAULT_FILM_FILTERS,
  FILM_FILTERS,
  FilmFilterKey,
  FilmFilterOptions,
  FilmFilterValue,
} from "./program-filters"
import { useProgramData } from "./useProgramData"
import { trackEvent } from "@features/tracking/tracking"

export type FilmSearchResultItem = {
  key: string
  film: BrowseFilmTileFragment
  session?: FilmSessionFragment
  letter?: string
}

export type FilmSearchMode = "featured" | "a-z" | "by-date" | "calendar"
export type FilmSearchModeDef = { label: string; uri: string; includeFilters: boolean }
export const BROWSE_MODES = {
  featured: { label: "Featured", uri: "/program/", includeFilters: false },
  "a-z": { label: "A-Z", uri: "/program/a-z/", includeFilters: true },
  "by-date": { label: "By Date", uri: "/program/by-date/", includeFilters: true },
  calendar: { label: "Calendar", uri: "/program/calendar/", includeFilters: false },
} satisfies Record<FilmSearchMode, FilmSearchModeDef>

export type FilmSearch = ReturnType<typeof useFilmSearch>

export function getFilterOptions(
  films: BrowseFilmTileFragment[],
  query: UseProgramFilmsQuery,
  mode: FilmSearchMode,
): {
  [K in FilmFilterKey]: FilmFilterOptions<K>
} {
  return Object.fromEntries(
    Object.entries(FILM_FILTERS).map(([key, def]) => [key, def.getOptions(films, query, mode)]),
  ) as any
}

/**
 * Track each individual filter option, but only once per page load.
 */
let trackedFilters = new Set<string>()
function trackFilterOption(key: FilmFilterKey, values: any) {
  try {
    if (Array.isArray(values)) {
      for (const value of values) {
        if (trackedFilters.has(key + value)) continue
        trackedFilters.add(key + value)
        trackEvent(
          "Select Filter",
          {
            FilterName: FILM_FILTERS[key].name,
            FilterValue: value,
            Area: "Program Filter",
          },
          document.getElementById("browse-page"),
        )
      }
    }
  } catch (e) {
    console.error(e)
  }
}

function normalizeValue(key: string, value: any) {
  const def = FILM_FILTERS[key as FilmFilterKey]
  value = value ?? def.getDefault()
  if (def.normalizeValue) {
    return def.normalizeValue(value)
  }
  return value
}

export function useFilmSearch({ mode = "a-z" }: { mode?: FilmSearchMode }) {
  const isHydrating = useHydrating()
  const lookup = useProgramData()
  const getMode = useCallbackRef(() => mode)

  const [params, paramsApi] = useSearchParams(DEFAULT_FILM_FILTERS)

  // Clean parameters
  useEffect(() => {
    let didClean = false
    for (let key in params) {
      const values = params[key as FilmFilterKey]
      if (Array.isArray(values) && values.some((v) => typeof v === "string" && v.endsWith("/"))) {
        for (const [i, v] of values.entries()) {
          values[i] = v.replace(/\/$/, "")
          didClean = true
        }
      }
    }
    if (didClean) {
      paramsApi.setAll(params)
    }
  }, [params])

  const router = useRouter()

  const getParam = (key: FilmFilterKey) => {
    const value = paramsApi.get(key)
    if (Array.isArray(DEFAULT_FILM_FILTERS[key])) {
      if (typeof value === "string" && !Array.isArray(value)) {
        paramsApi.set(key, [value])
        return paramsApi.get(key)
      }
    }
    return value
  }

  const filters = useMemo(() => {
    const maybeRedirect = () => {
      if (getMode() !== "featured") return
      setTimeout(() => {
        if (paramsApi.get("venues")?.length || paramsApi.get("date")?.length) {
          router.navigate("/program/by-date/" + document.location.search)
        } else {
          router.navigate("/program/a-z/" + document.location.search)
        }
      }, 1000)
    }

    const set = <K extends FilmFilterKey>(key: K, value: FilmFilterValue<K> | undefined) => {
      value = normalizeValue(key, value as any)
      paramsApi.set(key, value as any)
      if (value) trackFilterOption(key, normalizeValue(key, value))
      maybeRedirect()
    }
    const setAll = (values: Record<FilmFilterKey, FilmFilterValue<any>>) => {
      paramsApi.setAll(values as any)
      const mode = getMode()
      for (const key in values) {
        let value = normalizeValue(key, values[key as FilmFilterKey] as FilmFilterValue<FilmFilterKey>)
        value = value ?? FILM_FILTERS[key as FilmFilterKey].getDefault()
        trackFilterOption(key as FilmFilterKey, value)
        paramsApi.set(key as FilmFilterKey, value)
      }
      maybeRedirect()
    }
    return {
      get: getParam,
      set,
      setAll,
      setter: (key: FilmFilterKey) => (value: FilmFilterValue<typeof key>) => {
        set(key, value)
      },
    }
  }, [])

  const allFilms = useMemo(
    () => lookup.data?.currentProgram?.films?.filter(Boolean) as BrowseFilmTileFragment[],
    [lookup.data],
  )

  const filterHash = useMemo(() => {
    return hash([params, mode])
  }, [params, mode])

  const filterOptions = useMemo(() => {
    if (!allFilms) return {}
    return Object.fromEntries(
      Object.entries(FILM_FILTERS).map(([key, def]) => [key, def.getOptions(allFilms, lookup.data!, mode)]),
    ) as any
  }, [allFilms, mode]) as { [K in FilmFilterKey]: FilmFilterOptions<K> }

  const filterTags = useMemo(() => {
    if (!allFilms) return []
    return Object.entries(FILM_FILTERS).flatMap(([key, def]) => {
      return def.getFilterTags ? def.getFilterTags(paramsApi.get(key as any), (filterOptions as any)[key]) : []
    })
  }, [params, filterOptions])

  /** Track filter usage */
  useEffect(() => {
    if (filterTags.length === 0) return
    trackEvent("Program Filter", {
      Filters: filterTags
        .filter((tag) => !!tag.label)
        .map((tag) => ({ FilterName: tag.type ?? "None", FilterValue: tag.label })),
    })
  }, [filterTags])

  const [items, setItems] = useRestorableState<FilmSearchResultItem[]>("results", [])

  function getResults() {
    let items: FilmSearchResultItem[] = []

    if (!allFilms) {
      return
    }

    // Get the initial list of items
    if (mode === "by-date" || mode === "calendar") {
      items =
        allFilms
          ?.flatMap((film) => {
            if (!film?.sessions?.length) return []
            return film.sessions.map((session) => {
              return {
                film: film!,
                session: session!,
                key: film!.uri! + "-" + session?.id!,
              }
            })
          })
          .sort((a, b) => {
            return a.session.info?.dateTime?.localeCompare(b.session.info?.dateTime!)!
          })! || []
    } else {
      items = allFilms?.map((film) => ({
        film: film!,
        key: film!.uri!,
        letter:
          film.title
            ?.replace(/^(the|a|an)\s+/i, "")
            ?.charAt(0)
            .toUpperCase()
            .replace(/[^A-Z]/, "#") || "#",
      }))
    }

    // Build a list of filter functions which can be applied to each item
    const filmMatchers: ((film: BrowseFilmTileFragment) => boolean)[] = []
    const sessionMatchers: ((session: FilmSessionFragment) => boolean)[] = []
    for (const [key, def] of Object.entries(FILM_FILTERS)) {
      const value = params[key as keyof typeof params]
      const { matchFilm, matchSession, always } =
        def.getMatchers?.(value as any, filterOptions[key as keyof typeof filterOptions] as any) ?? {}
      if ((value && value.length > 0) || always) {
        if (matchFilm) {
          filmMatchers.push(matchFilm)
        }
        if (matchSession) {
          sessionMatchers.push(matchSession)
        }
      }
    }

    // Apply filters
    if (mode === "a-z" || mode === "by-date") {
      items = items.filter((item) => {
        // Ensure this film matches
        const filmMatch = filmMatchers.every((matcher) => {
          return matcher!(item.film)
        })
        if (!filmMatch) return false

        if (item.session) {
          // This tile represents a whole session, so test that single session
          return sessionMatchers.every((matcher) => {
            return matcher!(item.session!)
          })
        } else if (!item.film.sessions?.length) {
          // Films A-Z, with no session shown
          return true
        } else {
          // This tile represents a film (with one or more sessions), so ensure at least one session matches
          return item.film.sessions?.some((session) => {
            return sessionMatchers.every((matcher) => {
              return matcher!(session!)
            })
          })
        }
      })
    }
    return items
  }

  useEffect(() => {
    const items = getResults()
    if (items) {
      startTransition(() => {
        setItems(items)
      })
    }
  }, [filterHash, allFilms?.length, mode])

  return {
    status: lookup.status,
    filters: filters,
    filterTags,
    items,
    filterHash,
    filterOptions,
    removeFilter: (key: string, value: string) => {
      const filter = paramsApi.get(key as any)
      if (Array.isArray(filter)) {
        paramsApi.set(
          key as any,
          normalizeValue(
            key,
            filter.filter((v) => v !== value),
          ),
        )
      } else {
        paramsApi.set(key as any, normalizeValue(key, undefined))
      }
    },
  }
}
